import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Plug, Settings } from 'lucide-react'
import { Page } from '@/components/Page'
import { Button } from '@/components/ui/button'
import { Modal } from '@/components/Modal'
import { Toolbar } from '@/components/Toolbar'
import { useToast } from '@/hooks/useToast'
import { useAuth } from '@/auth/useAuth'
import { inboxApi } from '@/features/inbox/api'
import {
  useCategoryCounts,
  useDisconnectAccount,
  useInboxAccounts,
  useInboxCategories,
  useInboxEmails,
  useStartConnect,
  useSyncAccount,
} from '@/features/inbox/queries'
import type { InboxAccount } from '@/features/inbox/types'
import { AccountsPanel } from './components/AccountsPanel'
import { OnboardingCard } from './components/OnboardingCard'
import { CategoryCardsRow } from './components/CategoryCardsRow'
import { InboxEmailsTable } from './components/InboxEmailsTable'
import { EmailDetailModal } from './components/EmailDetailModal'
import {
  AttachmentPreviewModal,
  type AttachmentPreview,
} from './components/AttachmentPreviewModal'

function classifyPreview(mime: string, filename: string): AttachmentPreview['kind'] {
  const lower = filename.toLowerCase()
  if (mime.startsWith('application/pdf') || lower.endsWith('.pdf')) return 'pdf'
  if (mime.startsWith('image/')) return 'image'
  if (mime.includes('csv') || lower.endsWith('.csv')) return 'csv'
  if (
    mime.includes('spreadsheetml') ||
    mime.includes('ms-excel') ||
    lower.endsWith('.xlsx') ||
    lower.endsWith('.xlsm') ||
    lower.endsWith('.xls')
  )
    return 'xlsx'
  if (mime.startsWith('text/')) return 'text'
  return 'other'
}

export function InboxPage() {
  const { hasPermission } = useAuth()
  const { toast } = useToast()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [pendingDisconnect, setPendingDisconnect] = useState<InboxAccount | null>(null)
  const [activeCategory, setActiveCategory] = useState<string | null>(null)
  const [preview, setPreview] = useState<AttachmentPreview | null>(null)
  const [selectedEmailId, setSelectedEmailId] = useState<string | null>(null)

  const accountsQuery = useInboxAccounts()
  const countsQuery = useCategoryCounts()
  const categoriesQuery = useInboxCategories()
  const emailsQuery = useInboxEmails({
    categoryId: activeCategory || undefined,
    limit: 500,
  })
  const startConnect = useStartConnect()
  const syncAccount = useSyncAccount()
  const disconnectAccount = useDisconnectAccount()

  const canConnect = hasPermission('inbox.connect')
  const canSync = hasPermission('inbox.sync')
  const canDisconnect = hasPermission('inbox.disconnect')
  const canManageAutoSync = hasPermission('inbox.autosync.manage')
  const canOnboard = hasPermission('inbox.onboard')
  const canManageSettings =
    hasPermission('inbox.categories.manage') || hasPermission('inbox.rules.manage')

  useEffect(() => {
    const connected = searchParams.get('connected')
    const err = searchParams.get('inbox_error')
    if (connected) toast({ title: 'Inbox connected', description: connected })
    if (err)
      toast({ title: 'Inbox connection failed', description: err, variant: 'destructive' })
    if (connected || err) {
      const next = new URLSearchParams(searchParams)
      next.delete('connected')
      next.delete('inbox_error')
      setSearchParams(next, { replace: true })
    }
  }, [searchParams, setSearchParams, toast])

  const accounts = accountsQuery.data?.accounts ?? []
  const counts = countsQuery.data?.counts ?? []
  const total = countsQuery.data?.total ?? 0
  const emails = emailsQuery.data?.emails ?? []
  const categories = categoriesQuery.data?.categories ?? []
  const activeCategoryName = activeCategory
    ? counts.find((c) => c.slug === activeCategory || c.category_id === activeCategory)?.name ??
      'Emails'
    : 'All mail'

  const onConnect = async () => {
    try {
      const resp = await startConnect.mutateAsync()
      window.location.href = resp.auth_url
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Could not start OAuth'
      toast({ title: 'Could not start connection', description: msg, variant: 'destructive' })
    }
  }

  const onSync = async (account: InboxAccount) => {
    try {
      const r = await syncAccount.mutateAsync({ id: account.id })
      toast({
        title:
          r.saved > 0
            ? `${r.saved} new email${r.saved === 1 ? '' : 's'}`
            : 'No new emails',
        description:
          r.saved > 0
            ? `${r.classified} sorted · ${r.extracted} extracted${r.quota_exhausted ? ' · AI quota reached' : ''}`
            : 'Your inbox is already up to date.',
      })
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Sync failed'
      toast({ title: 'Sync failed', description: msg, variant: 'destructive' })
    }
  }

  const onDisconnect = async () => {
    if (!pendingDisconnect) return
    try {
      await disconnectAccount.mutateAsync(pendingDisconnect.id)
      toast({ title: 'Account disconnected', description: pendingDisconnect.email })
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Disconnect failed'
      toast({ title: 'Disconnect failed', description: msg, variant: 'destructive' })
    } finally {
      setPendingDisconnect(null)
    }
  }

  const onOpenAttachment = async (attachmentId: string, hintMime: string | null) => {
    try {
      const resp = await inboxApi.attachmentDownload(attachmentId)
      const mime = resp.mime_type || hintMime || ''
      const kind = classifyPreview(mime, resp.filename || '')
      if (kind === 'pdf' || kind === 'image') {
        setPreview({ url: resp.url, filename: resp.filename, mime, kind })
        return
      }
      if (kind === 'csv' || kind === 'xlsx' || kind === 'text') {
        setPreview({ url: resp.url, filename: resp.filename, mime, kind, loading: true })
        try {
          const parsed = await inboxApi.attachmentPreviewText(attachmentId)
          setPreview({ url: resp.url, filename: resp.filename, mime, kind, text: parsed.text })
        } catch (err) {
          const msg = err instanceof Error ? err.message : 'Preview failed'
          toast({ title: 'Preview failed', description: msg, variant: 'destructive' })
          setPreview({ url: resp.url, filename: resp.filename, mime, kind, text: '' })
        }
        return
      }
      window.open(resp.url, '_blank', 'noopener,noreferrer')
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Open failed'
      toast({ title: 'Could not open file', description: msg, variant: 'destructive' })
    }
  }

  return (
    <Page
      title="Inbox"
      description="Connects your mailbox, auto-syncs on your schedule, and sorts every email into a category. Nothing reaches your books until you accept it here."
      actions={
        <Toolbar align="end">
          {canManageSettings ? (
            <Button variant="secondary" onClick={() => navigate('/inbox/settings')}>
              <Settings className="size-4" />
              Categories & rules
            </Button>
          ) : null}
          {canConnect ? (
            <Button onClick={onConnect} disabled={startConnect.isPending}>
              <Plug className="size-4" />
              {startConnect.isPending
                ? 'Starting…'
                : accounts.length === 0
                  ? 'Connect Gmail'
                  : 'Add account'}
            </Button>
          ) : null}
        </Toolbar>
      }
    >
      <AccountsPanel
        accounts={accounts}
        loading={accountsQuery.isLoading}
        canConnect={canConnect}
        canSync={canSync}
        canDisconnect={canDisconnect}
        canManageAutoSync={canManageAutoSync}
        connectPending={startConnect.isPending}
        syncPending={syncAccount.isPending}
        onConnect={onConnect}
        onSync={onSync}
        onRequestDisconnect={setPendingDisconnect}
      />

      {accounts
        .filter((a) => a.status === 'connected' && a.onboarding_status === 'pending')
        .map((a) => (
          <OnboardingCard key={a.id} account={a} canOnboard={canOnboard} />
        ))}

      {accounts.length > 0 ? (
        <CategoryCardsRow
          counts={counts}
          total={total}
          loading={countsQuery.isLoading}
          activeCategoryId={activeCategory}
          onSelect={setActiveCategory}
        />
      ) : null}

      {accounts.length > 0 ? (
        <InboxEmailsTable
          emails={emails}
          categories={categories}
          loading={emailsQuery.isLoading}
          title={activeCategoryName}
          hasAccounts={accounts.length > 0}
          onSelectEmail={setSelectedEmailId}
          onOpenAttachment={onOpenAttachment}
        />
      ) : null}

      <EmailDetailModal
        emailId={selectedEmailId}
        onClose={() => setSelectedEmailId(null)}
        onOpenAttachment={onOpenAttachment}
      />

      <AttachmentPreviewModal preview={preview} onClose={() => setPreview(null)} />

      <Modal.Confirm
        open={pendingDisconnect !== null}
        onOpenChange={(open) => !open && setPendingDisconnect(null)}
        title="Disconnect inbox account?"
        body={
          <p>
            This will remove the connection to{' '}
            <span className="font-medium">{pendingDisconnect?.email}</span> and delete its
            stored emails, attachments, and extracted items. You can reconnect anytime.
          </p>
        }
        confirmLabel="Disconnect"
        confirmVariant="danger"
        onConfirm={onDisconnect}
        busy={disconnectAccount.isPending}
      />
    </Page>
  )
}
